import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, AlertTriangle, Save, Loader2, Search, Truck, User,
  MapPin, FileText, Upload, X, Calendar, Link2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const font = { barlow: { fontFamily: 'Barlow Condensed, Barlow, sans-serif' }, mono: { fontFamily: 'JetBrains Mono, monospace' } };

const INCIDENT_TYPES = [
  { value: 'accident', label: 'Accident' },
  { value: 'panne', label: 'Panne' },
  { value: 'vol', label: 'Vol' },
  { value: 'perte_marchandise', label: 'Perte marchandise' },
  { value: 'retard', label: 'Retard' },
  { value: 'dommage_marchandise', label: 'Dommage marchandise' },
  { value: 'probleme_douane', label: 'Probleme douane' },
  { value: 'probleme_client', label: 'Probleme client' },
  { value: 'probleme_chauffeur', label: 'Probleme chauffeur' },
  { value: 'autre', label: 'Autre' },
];

const SEVERITY_OPTS = [
  { value: 'critique', label: 'Critique (P1)', cls: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'elevee', label: 'Elevee (P2)', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'moyenne', label: 'Moyenne (P3)', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'faible', label: 'Basse (P4)', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

const PRIORITY_OPTS = [
  { value: 'haute', label: 'Haute' },
  { value: 'normale', label: 'Normale' },
  { value: 'basse', label: 'Basse' },
];

/* ─── Combobox-like searchable select ──────────────────── */
const SearchSelect = ({ items, value, onSelect, placeholder, renderItem, searchKey, testId }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    if (!query) return items.slice(0, 20);
    const q = query.toLowerCase();
    return items.filter(i => {
      const searchStr = typeof searchKey === 'function' ? searchKey(i) : (i[searchKey] || '');
      return searchStr.toLowerCase().includes(q);
    }).slice(0, 20);
  }, [items, query, searchKey]);

  const selected = items.find(i => i.id === value);

  return (
    <div className="relative" data-testid={testId}>
      <div
        className="flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:border-emerald-300 transition-colors bg-white text-sm"
        onClick={() => setOpen(!open)}
      >
        {selected ? (
          <div className="flex-1 min-w-0">
            <span className="truncate">{renderItem ? renderItem(selected) : selected.name || selected.id}</span>
          </div>
        ) : (
          <span className="text-slate-400 flex-1">{placeholder}</span>
        )}
        {value && (
          <button onClick={(e) => { e.stopPropagation(); onSelect(null); }} className="text-slate-400 hover:text-red-500">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </div>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-hidden">
          <div className="p-2 border-b">
            <Input
              value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Rechercher..." className="text-sm h-8"
              autoFocus
            />
          </div>
          <div className="overflow-auto max-h-48">
            {filtered.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Aucun resultat</p>}
            {filtered.map(item => (
              <div
                key={item.id}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 transition-colors
                  ${item.id === value ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700'}`}
                onClick={() => { onSelect(item.id); setOpen(false); setQuery(''); }}
              >
                {renderItem ? renderItem(item) : item.name || item.id}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CREATE INCIDENT PAGE
   ═══════════════════════════════════════════════════════════ */
const CreateIncidentPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    cause: '', description: '', incident_type: 'retard',
    severity: 'moyenne', priority: 'normale',
    location: '', occurred_at: '',
    operation_id: null, transfer_id: null, vehicle_id: null, driver_id: null,
  });

  // Context data
  const [operations, setOperations] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const headers = useMemo(() => ({ Authorization: 'Bearer cookie-auth' }), []);

  // Fetch context data
  useEffect(() => {
    const fetch = async () => {
      try {
        const [opsRes, vRes, dRes] = await Promise.all([
          axios.get(`${API}/transport-requests?limit=100`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/vehicles?limit=200`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/drivers?limit=200`, { headers }).catch(() => ({ data: [] })),
        ]);
        const ops = opsRes.data?.requests || opsRes.data?.items || opsRes.data || [];
        setOperations(Array.isArray(ops) ? ops : []);
        const vehs = vRes.data?.vehicles || vRes.data?.items || vRes.data || [];
        setVehicles(Array.isArray(vehs) ? vehs : []);
        const drvs = dRes.data?.drivers || dRes.data?.items || dRes.data || [];
        setDrivers(Array.isArray(drvs) ? drvs : []);
      } catch { /* ignore */ }
    };
    fetch();
  }, [headers]);

  // Fetch transfers when operation is selected
  useEffect(() => {
    if (!form.operation_id) { setTransfers([]); return; }
    axios.get(`${API}/operations/${form.operation_id}/children`, { headers })
      .then(r => setTransfers(r.data || []))
      .catch(() => setTransfers([]));
  }, [form.operation_id, headers]);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));

  const handleSubmit = async () => {
    if (!form.cause || form.cause.length < 5) {
      toast({ title: 'La cause doit faire au moins 5 caracteres', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === null) delete payload[k]; });
      if (payload.occurred_at) payload.occurred_at = new Date(payload.occurred_at).toISOString();
      await axios.post(`${API}/incidents`, payload, { headers });
      toast({ title: 'Incident créé avec succès' });
      navigate('/admin/incidents');
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur creation', variant: 'destructive' });
    }
    setSaving(false);
  };

  const sevOpt = SEVERITY_OPTS.find(s => s.value === form.severity);

  return (
    <div className="min-h-full bg-slate-50" data-testid="create-incident-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-20">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/incidents')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight" style={font.barlow}>Nouvel incident</h1>
              <p className="text-xs text-slate-500">Signalez un problème sur une opération</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/admin/incidents')}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={saving || !form.cause || form.cause.length < 5}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-incident-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Creer l'incident
            </Button>
          </div>
        </div>
      </div>

      {/* Content - 2 columns */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* ─── Left: Essentiel (3/5) ──────────────────── */}
          <div className="lg:col-span-3 space-y-5">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={font.barlow}>
                  <AlertTriangle className="w-4 h-4 text-red-500" /> Informations essentielles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Cause */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Cause / Titre de l'incident *</Label>
                  <Input value={form.cause} onChange={set('cause')} placeholder="Ex: Pneu eclate sur le trajet Abidjan-San Pedro"
                    className="text-sm" data-testid="incident-cause" />
                  {form.cause && form.cause.length < 5 && (
                    <p className="text-[10px] text-red-500">Minimum 5 caracteres</p>
                  )}
                </div>
                {/* Type + Severity + Priority */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Type *</Label>
                    <Select value={form.incident_type} onValueChange={set('incident_type')}>
                      <SelectTrigger className="text-sm" data-testid="incident-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {INCIDENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Severite *</Label>
                    <Select value={form.severity} onValueChange={set('severity')}>
                      <SelectTrigger className="text-sm" data-testid="incident-severity"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SEVERITY_OPTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Priorite</Label>
                    <Select value={form.priority} onValueChange={set('priority')}>
                      <SelectTrigger className="text-sm" data-testid="incident-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Severity indicator */}
                {sevOpt && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${sevOpt.cls}`}>
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-xs font-bold">{sevOpt.label}</span>
                    <span className="text-[10px] ml-auto">
                      {form.severity === 'critique' ? 'SLA: 1h reponse / 4h resolution' :
                       form.severity === 'elevee' ? 'SLA: 4h reponse / 24h resolution' :
                       form.severity === 'moyenne' ? 'SLA: 8h reponse / 48h resolution' : 'SLA: 24h reponse / 72h resolution'}
                    </span>
                  </div>
                )}
                {/* Description */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Description détaillée</Label>
                  <Textarea value={form.description} onChange={set('description')} rows={4}
                    placeholder="Decrivez l'incident en detail: circonstances, dommages constates, actions immediates prises..."
                    className="text-sm" data-testid="incident-description" />
                </div>
                {/* Location + Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium flex items-center gap-1"><MapPin className="w-3 h-3" /> Lieu</Label>
                    <Input value={form.location} onChange={set('location')} placeholder="Ex: PK 145, Autoroute du Nord"
                      className="text-sm" data-testid="incident-location" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium flex items-center gap-1"><Calendar className="w-3 h-3" /> Date/heure</Label>
                    <Input type="datetime-local" value={form.occurred_at} onChange={set('occurred_at')}
                      className="text-sm" data-testid="incident-occurred-at" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ─── Right: Contexte (2/5) ──────────────────── */}
          <div className="lg:col-span-2 space-y-5">
            {/* Operation / Transfert */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={font.barlow}>
                  <Link2 className="w-4 h-4 text-blue-500" /> Contexte opération
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Opération liee</Label>
                  <SearchSelect
                    items={operations}
                    value={form.operation_id}
                    onSelect={v => setForm(p => ({ ...p, operation_id: v, transfer_id: null }))}
                    placeholder="Rechercher une operation..."
                    searchKey={i => `${i.operationNumber || ''} ${i.reference || ''} ${i.clientName || ''}`}
                    renderItem={i => (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400" style={font.mono}>{i.operationNumber || i.reference || i.id?.slice(0, 8)}</span>
                        <span className="truncate text-xs">{i.clientName || ''}</span>
                      </div>
                    )}
                    testId="select-operation"
                  />
                </div>
                {transfers.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Transfert lie</Label>
                    <SearchSelect
                      items={transfers}
                      value={form.transfer_id}
                      onSelect={v => setForm(p => ({ ...p, transfer_id: v }))}
                      placeholder="Choisir un transfert..."
                      searchKey={i => `${i.reference || ''} ${i.plannedVehiclePlate || ''}`}
                      renderItem={i => (
                        <span className="text-xs">{i.reference || i.runNumber || i.id?.slice(0, 8)} - {i.plannedVehiclePlate || ''}</span>
                      )}
                      testId="select-transfer"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Véhicule / Chauffeur */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold flex items-center gap-2" style={font.barlow}>
                  <Truck className="w-4 h-4 text-emerald-500" /> Véhicule & Chauffeur
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Véhicule implique</Label>
                  <SearchSelect
                    items={vehicles}
                    value={form.vehicle_id}
                    onSelect={v => setForm(p => ({ ...p, vehicle_id: v }))}
                    placeholder="Rechercher un vehicule..."
                    searchKey={i => `${i.plateNumber || i.registration || ''} ${i.brand || ''} ${i.genre || ''}`}
                    renderItem={i => (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold" style={font.mono}>{i.plateNumber || i.registration || '?'}</span>
                        <span className="text-[10px] text-slate-400">{i.brand || ''} {i.genre ? `· ${i.genre}` : ''}</span>
                      </div>
                    )}
                    testId="select-vehicle"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Chauffeur implique</Label>
                  <SearchSelect
                    items={drivers}
                    value={form.driver_id}
                    onSelect={v => setForm(p => ({ ...p, driver_id: v }))}
                    placeholder="Rechercher un chauffeur..."
                    searchKey={i => `${i.firstName || ''} ${i.lastName || ''} ${i.phone || ''} ${i.permitNumber || ''}`}
                    renderItem={i => (
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3 text-slate-400" />
                        <span className="text-xs">{i.firstName} {i.lastName}</span>
                        <span className="text-[10px] text-slate-400">{i.permitType || ''}</span>
                      </div>
                    )}
                    testId="select-driver"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="p-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Résumé</p>
                <div className="space-y-1 text-xs text-slate-600">
                  <p><strong>Type :</strong> {INCIDENT_TYPES.find(t => t.value === form.incident_type)?.label}</p>
                  <p><strong>Severite :</strong> <Badge className={`text-[9px] ${sevOpt?.cls || ''} border ml-1`}>{sevOpt?.label}</Badge></p>
                  {form.location && <p><strong>Lieu :</strong> {form.location}</p>}
                  {form.operation_id && <p><strong>Opération :</strong> {operations.find(o => o.id === form.operation_id)?.operationNumber || form.operation_id.slice(0, 8)}</p>}
                  {form.vehicle_id && <p><strong>Véhicule :</strong> {vehicles.find(v => v.id === form.vehicle_id)?.plateNumber || vehicles.find(v => v.id === form.vehicle_id)?.registration || '?'}</p>}
                  {form.driver_id && <p><strong>Chauffeur :</strong> {(() => { const d = drivers.find(dr => dr.id === form.driver_id); return d ? `${d.firstName} ${d.lastName}` : '?'; })()}</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateIncidentPage;
